import '@/components/MapOverview/map.css';
import 'mapbox-gl/dist/mapbox-gl.css';

import React, { ComponentProps } from 'react';
import { Map as MapGlMap, MapRef } from 'react-map-gl';

import { useMapTokenRefresh } from '@/components/widgets/o/Map/hooks/useMapTokenRefresh';
import { DEFAULT_MAP_STYLE, MAPBOX_TOKEN } from '@/config/constants';
import { fontFamily } from '@/theme/fontFamily';
import { extractRequestParams, getProxiedMapServerUrl } from '@/utils/mapServer';

const SuspendedMap = React.forwardRef<MapRef, ComponentProps<typeof MapGlMap>>(({ style, ...delegated }, ref) => {
  useMapTokenRefresh();

  const handleTransformRequest = (rawUrl: string) => {
    if (rawUrl.includes('mapserver.landler.io')) {
      const { url, method, body } = extractRequestParams(getProxiedMapServerUrl(rawUrl));
      return {
        url,
        method,
        ...(body && { body, headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }),
        credentials: 'include' as const,
      };
    }
    return { url: rawUrl };
  };

  return (
    <MapGlMap
      ref={ref}
      mapStyle={DEFAULT_MAP_STYLE}
      mapboxAccessToken={MAPBOX_TOKEN}
      dragRotate={false}
      touchPitch={false}
      cursor='default'
      style={{ fontFamily: fontFamily.sans, ...style }}
      logoPosition='bottom-right'
      attributionControl={false}
      transformRequest={handleTransformRequest}
      {...delegated}
    />
  );
});
SuspendedMap.displayName = 'SuspendedMap';

export const Map = React.forwardRef<MapRef, ComponentProps<typeof MapGlMap>>((props, ref) => {
  return (
    <React.Suspense
      fallback={
        <div className='flex items-center justify-center h-full w-full min-h-[400px] bg-gray-50'>
          <p className='text-gray-500 animate-pulse'>Initializing map...</p>
        </div>
      }
    >
      <SuspendedMap {...props} ref={ref} />
    </React.Suspense>
  );
});
Map.displayName = 'Map';

export * from './hooks/useJustifyMap';
